"use client";

import { useState, useRef, useEffect } from "react";
import { Search } from "lucide-react";
import { Datepicker } from "flowbite-react";
import Filters from "../../components/common/Filters";
import filter from "../../assets/icons/Filter.svg";
import scan from "../../assets/icons/scan.svg";
import QrPopup from "../../components/Popup/QrPopup";
import { ChevronDown, ChevronUp } from "lucide-react";
import dayjs from "dayjs";
import { useAuth } from "../../context/AuthContext";

// Updated to match the structure used in other components
const userRoles = [
  { id: 1, name: "Admin" },
  { id: 2, name: "Incorporator" },
  { id: 3, name: "Sub Operator" },
  { id: 4, name: "General Coordinator" },
  { id: 5, name: "Coordinator" },
  { id: 6, name: "Sub Coordinator" },
  { id: 7, name: "Agent" },
  { id: 8, name: "Sub Agent" },
  { id: 9, name: "Player" }
];

// Add this custom hook at the top of the file
const useClickOutside = (ref, callback) => {
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        callback();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [ref, callback]);
};

export default function TransactionSearch({ gameSettings, onFilterChange, currentFilters }) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [qrPopup, setQrPopup] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [gameDropdownOpen, setGameDropdownOpen] = useState(false);
  const [drawTimeDropdownOpen, setDrawTimeDropdownOpen] = useState(false);
  const [userTypeDropdownOpen, setUserTypeDropdownOpen] = useState(false);
  
  const gameDropdownRef = useRef(null);
  const drawTimeDropdownRef = useRef(null);
  const userTypeDropdownRef = useRef(null);
  const { user } = useAuth();
  
  // Filter roles to only show roles below the current user's role
  const filteredUserRoles = userRoles.filter(role => 
    role.id > user?.roleId
  );

  const displayRoles = [{ id: -1, name: "All" }, { id: 0, name: "Own" }, ...filteredUserRoles];

  // Game dropdown handling
  useClickOutside(gameDropdownRef, () => setGameDropdownOpen(false));
  // Draw time dropdown handling
  useClickOutside(drawTimeDropdownRef, () => setDrawTimeDropdownOpen(false));
  // User type dropdown handling
  useClickOutside(userTypeDropdownRef, () => setUserTypeDropdownOpen(false));

  const getSelectedGameData = () =>
    gameSettings.find(game => game.id === currentFilters.gameId);

  // Update handler for game selection
  const handleGameSelect = (gameId) => {
    onFilterChange({
      ...currentFilters,
      gameId,
      drawTime: ""
    });
  };

  // Update handler for draw time
  const handleDrawTimeSelect = (time) => {
    onFilterChange({
      ...currentFilters,
      drawTime: time
    });
  };

  // Update handler for user type
  const handleUserTypeSelect = (roleId) => {
    onFilterChange({
      ...currentFilters,
      type: roleId === -1 ? "" : roleId
    });
    setUserTypeDropdownOpen(false);
  };

  // Update handler for date selection
  const handleDateSelect = (date) => {
    try {
      // Ensure we have a valid date and convert it properly
      if (date) {
        // Force date to be treated as a Date object
        const dateObj = new Date(date);
        const formattedDate = dayjs(dateObj).format("YYYY-MM-DD");
        console.log("Date selected and formatted:", formattedDate);
        
        // Apply the filter change with the formatted date
        onFilterChange({
          ...currentFilters,
          drawDate: formattedDate,
          page: 1
        });
      } else {
        onFilterChange({
          ...currentFilters,
          drawDate: '',
          page: 1
        });
      }
    } catch (error) {
      console.error("Error processing date:", error);
    }
  };

  useEffect(() => {
    console.log(currentFilters);
  }, [currentFilters]);

  return (
    <>
      <div className="space-y-4 mb-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className={isFilterOpen ? "bg-blue-50 text-blue-600" : ""}
          >
            <img
              src={filter}
              alt="filter"
              width={42}
              height={42}
              className="rounded-lg"
            />
          </button>
          <div className="flex-1 relative p-2 border border-[#E1E7EF] rounded-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 " />
            <input
              placeholder="Search by ticket ID, name, email or no."
              className="pl-8 w-full text-xs font-normal outline-none focus:border-transparent"
              value={currentFilters.search}
              onChange={(e) => onFilterChange({...currentFilters, search: e.target.value})}
            />
          </div>
          <button variant="outline" size="icon"
            onClick={() => setQrPopup(true)}
          >
            <img
              src={scan}
              alt="filter"
              width={42}
              height={42}
              className="rounded-lg"
            />{" "}
          </button>
        </div>

        {isFilterOpen && (
          <>
            <div className="flex gap-4">
              <div className="space-y-2 flex-1">
                <label htmlFor="date" className="text-xs font-medium">
                  Draw Date
                </label>
                <Datepicker
                  id="date"
                  value={currentFilters.drawDate ? new Date(currentFilters.drawDate) : null}
                  onChange={(date) => handleDateSelect(date)}
                  placeholder="Select date"
                  theme={{
                    root: {
                      input: {
                        base: "w-full"
                      }
                    },
                    "months": {
                      "items": {
                        "base": "grid w-64 grid-cols-4",
                        "item": {
                          "base": "block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600",
                          "selected": "bg-cyan-700 text-white hover:bg-cyan-600",
                          "disabled": "text-gray-500"
                        }
                      }
                    },
                  }}
                />
              </div>

              <div className="space-y-2 flex-1">
                <label htmlFor="game" className="text-xs font-medium ">
                  Select Game
                </label>
                <div className="relative w-full" ref={gameDropdownRef}>
                  <button
                    className="w-full p-2.5 text-xs font-normal text-gray-700 bg-white border rounded-md shadow-sm flex justify-between items-center"
                    onClick={() => setGameDropdownOpen(!gameDropdownOpen)}
                  >
                    <span>{getSelectedGameData()?.name || "All"}</span>
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  </button>
                  {gameDropdownOpen && (
                    <ul className="absolute left-0 z-10 w-full mt-1 bg-white border rounded-md shadow-md">
                      {gameSettings.map((game) => (
                        <li
                          key={game.id}
                          onClick={() => handleGameSelect(game.id)}
                          className="p-2.5 text-xs text-black hover:bg-[#E1E7EF] cursor-pointer"
                        >
                          {game.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="space-y-2 flex-1">
                <label className="text-xs font-medium">Select Draw Time</label>
                <div className="relative w-full" ref={drawTimeDropdownRef}>
                  <button
                    className="w-full p-2.5 text-xs font-normal text-gray-700 bg-white border rounded-md shadow-sm flex justify-between items-center"
                    onClick={() => setDrawTimeDropdownOpen(!drawTimeDropdownOpen)}
                    disabled={!currentFilters.gameId}
                  >
                    <span>{currentFilters.drawTime || "All"}</span>
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  </button>
                  {drawTimeDropdownOpen && currentFilters.gameId && (
                    <ul className="absolute left-0 z-10 w-full mt-1 bg-white border rounded-md shadow-md">
                      {getSelectedGameData()?.otherSettings?.drawTime?.map((time) => (
                        <li
                          key={time}
                          onClick={() => handleDrawTimeSelect(time)}
                          className="p-2.5 text-xs text-black hover:bg-[#E1E7EF] cursor-pointer"
                        >
                          {dayjs(`1970-01-01T${time}`).format('h:mm A')}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="space-y-2 flex-1">
                <label htmlFor="game" className="text-xs font-medium ">
                  User Type
                </label>
                <div className="relative w-full" ref={userTypeDropdownRef}>
                  <button
                    id="dropdownButton"
                    className="w-full p-2.5 text-xs font-normal text-gray-700 bg-white border rounded-md shadow-sm flex justify-between items-center "
                    onClick={() => setUserTypeDropdownOpen(!userTypeDropdownOpen)}
                  >
                    <span>{displayRoles.find(role => role.id === (currentFilters.type === "" ? -1 : currentFilters.type))?.name || "All"}</span>
                    <span className="text-gray-500">
                      {userTypeDropdownOpen ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </span>
                  </button>
                  {userTypeDropdownOpen && (
                    <ul className="absolute left-0 z-10 w-full mt-1 bg-white border rounded-md shadow-md">
                      {displayRoles.map((role) => (
                        <li
                          key={role.id}
                          onClick={() => handleUserTypeSelect(role.id)}
                          className="p-2.5 text-xs text-black hover:bg-[#E1E7EF] cursor-pointer"
                        >
                          {role.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

            </div>
          </>
        )}
      </div>

      <QrPopup isOpen={qrPopup} onClose={() => setQrPopup(false)} />
    </>
  );
}
